/**
 * P4 作废申请审批（manager / director / admin）
 *  - 待审批列表 + 24h 倒计时
 *  - 同意 / 拒绝 + 备注
 */
import { useState } from 'react';
import { Modal, Input, Tabs, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { fmtMonthDayTime } from '../../lib/format';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';

export function VoidageRequestsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pending');
  const [reviewing, setReviewing] = useState<any>(null);
  const [decision, setDecision] = useState<'approved' | 'rejected'>('approved');
  const [notes, setNotes] = useState('');

  const url = tab === 'pending' ? '/customer-voidage/pending' : `/customer-voidage?status=${tab}`;
  const { data: rows } = useQuery<any[]>({
    queryKey: ['voidage', tab],
    queryFn: () => $.get(url),
    refetchInterval: 30_000,
  });

  const { data: cfgInfo } = useQuery<any>({
    queryKey: ['voidage-config-info'],
    queryFn: () => $.get('/customer-voidage/config-info'),
    staleTime: 60_000,
  });
  const reviewHours = Number(cfgInfo?.voidage_review_hours ?? 24);

  const review = useMutation({
    mutationFn: () => $.post(`/customer-voidage/${reviewing.id}/review`, { decision, notes }),
    onSuccess: () => {
      message.success(decision === 'approved' ? '已同意作废' : '已拒绝');
      setReviewing(null); setNotes('');
      qc.invalidateQueries({ queryKey: ['voidage'] });
    },
    onError: (e: any) => message.error(e?.message ?? '失败'),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['主管', '作废审批']}
        title="🗑 客户作废审批"
        subtitle={`销售员尝试加微信 ≥ 阈值次仍失败可申请作废 · 主管超 ${reviewHours}h 未审批系统自动通过`}
      />
      <div className="work-content">
        <Tabs activeKey={tab} onChange={setTab}
              items={[
                { key: 'pending', label: `待审批（${rows?.length ?? 0}）` },
                { key: 'approved', label: '已同意' },
                { key: 'rejected', label: '已拒绝' },
                { key: 'auto_approved', label: '自动通过' },
              ]} />
        <HomeCard title="申请列表">
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>客户</th>
                  <th>申请人</th>
                  <th>申请时间</th>
                  <th>剩余时间</th>
                  <th>理由</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无</td></tr>
                )}
                {(rows ?? []).map((r: any) => {
                  const elapsed = (Date.now() - new Date(r.requestedAt).getTime()) / 3_600_000;
                  const remaining = Math.max(0, reviewHours - elapsed);
                  const remTone: any = remaining < 4 ? 'danger' : remaining < 12 ? 'warning' : 'success';
                  return (
                    <tr key={r.id}>
                      <td>{r.customerName ?? `#${r.customerId}`}</td>
                      <td>{r.requestedByName ?? `#${r.requestedByUserId}`}</td>
                      <td>{fmtMonthDayTime(r.requestedAt)}</td>
                      <td>
                        {r.status === 'pending'
                          ? <Pill tone={remTone}>{remaining.toFixed(1)}h</Pill>
                          : '—'}
                      </td>
                      <td style={{ maxWidth: 240, whiteSpace: 'normal' }}>{r.reason ?? '—'}</td>
                      <td><Pill tone={r.status === 'pending' ? 'warning' : r.status === 'approved' || r.status === 'auto_approved' ? 'success' : 'danger'}>{r.status}</Pill></td>
                      <td>
                        {r.status === 'pending' && (
                          <>
                            <Btn kind="primary" onClick={() => { setReviewing(r); setDecision('approved'); }}>✓ 同意</Btn>
                            <Btn kind="default" onClick={() => { setReviewing(r); setDecision('rejected'); }}>✗ 拒绝</Btn>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <Modal
        title={decision === 'approved' ? '同意作废申请' : '拒绝作废申请'}
        open={!!reviewing}
        onCancel={() => { setReviewing(null); setNotes(''); }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setReviewing(null); setNotes(''); }}>取消</Btn>
            <Btn kind="primary" onClick={() => review.mutate()} disabled={review.isPending}>
              {review.isPending ? '提交中…' : '确认'}
            </Btn>
          </>
        }
      >
        <div style={{ marginBottom: 10, fontSize: 13 }}>
          {decision === 'approved'
            ? '✓ 同意后：客户置为已作废，销售员退还一个持客名额'
            : '✗ 拒绝后：销售员需继续尝试加微信，可重新申请'}
        </div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>主管审批备注</div>
        <Input.TextArea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Modal>
    </>
  );
}
